import React, { useState, useEffect } from 'react';
import { 
  getSubTeams, 
  createSubTeam, 
  deleteSubTeam, 
  updateSubTeam,
  getTeamsByOrganization,
  getOrganization
} from '../../api';
import './SubTeamManagement.css';

function GlobalSubTeamManagement({ 
  organization, 
  user, 
  onBack,
  organizationId
}) {
  const [subTeams, setSubTeams] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateSubTeam, setShowCreateSubTeam] = useState(false);
  const [newSubTeamName, setNewSubTeamName] = useState('');
  const [newSubTeamDescription, setNewSubTeamDescription] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [editingSubTeam, setEditingSubTeam] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [organizationDetails, setOrganizationDetails] = useState(null);

  // Organisation aus user ermitteln, falls nicht als Prop übergeben
  // Priorität: organizationDetails > organizationId Prop > organization Prop > user.organization_id
  const currentOrganization = organizationDetails || organization || {
    id: organizationId || user?.organization_id,
    name: user?.organization_name || 'Organisation'
  };

  const isAdmin = user?.isAdmin || false;
  const isOrgLeader = user?.isOrgLeader || false;
  
  // Responsive Detection
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive Styles
  const styles = {
    container: {
      margin: '0 auto',
      maxWidth: '1400px',
      padding: isMobile ? '5px' : '15px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: '15px',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '10px' : '0',
    },
    title: {
      color: '#2d3748',
      fontSize: isMobile ? '1.2rem' : '1.6rem',
      margin: 0,
    },
    backButton: {
      padding: '8px 16px',
      background: '#4a6de5',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '14px',
      marginBottom: isMobile ? '10px' : '0',
      transition: 'background-color 0.2s',
      width: 'auto',
      minWidth: 'fit-content',
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '15px',
    },
    sectionTitle: {
      fontSize: isMobile ? '1.1rem' : '1.3rem',
      color: '#2d3748',
      margin: 0,
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      flexDirection: isMobile ? 'column' : 'row',
    },
    createButton: {
      padding: '8px 16px',
      background: '#0a9a22',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '14px',
      transition: 'background-color 0.2s',
      width: 'auto',
      minWidth: 'fit-content',
    },
    createForm: {
      background: 'white',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      padding: isMobile ? '10px' : '15px',
      marginBottom: '15px',
      display: 'flex',
      gap: '8px',
      flexDirection: isMobile ? 'column' : 'row',
    },
    formInput: {
      padding: '6px 8px',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '0.8rem',
      flex: isMobile ? '1' : 'auto',
      minWidth: isMobile ? 'auto' : '200px',
    },
    formSelect: {
      padding: '6px 8px',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '0.8rem',
      flex: isMobile ? '1' : 'auto',
      minWidth: isMobile ? 'auto' : '200px',
    },
    formTextarea: {
      padding: '6px 8px',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '0.8rem',
      flex: isMobile ? '1' : 'auto',
      minWidth: isMobile ? 'auto' : '200px',
      resize: 'vertical',
      minHeight: '60px',
    },
    formButton: {
      padding: '6px 12px',
      border: 'none',
      borderRadius: '4px',
      fontSize: '0.8rem',
      cursor: 'pointer',
      fontWeight: '500',
    },
    submitButton: {
      background: '#0a9a22',
      color: 'white',
    },
    cancelButton: {
      background: '#f3f4f6',
      color: '#4b5563',
      border: '1px solid #d1d5db',
    },
    // Sub-Team-Karten Styles
    subTeamsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
      gap: isMobile ? '12px' : '16px',
      marginBottom: '20px',
    },
    subTeamCard: {
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      padding: isMobile ? '16px' : '20px',
      border: '1px solid #e2e8f0',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
    },
    subTeamCardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px',
    },
    subTeamName: {
      fontSize: isMobile ? '1rem' : '1.1rem',
      fontWeight: '600',
      color: '#2d3748',
      margin: 0,
      lineHeight: '1.3',
      flex: 1,
    },
    subTeamDescription: {
      fontSize: isMobile ? '0.8rem' : '0.9rem',
      color: '#718096',
      marginBottom: '16px',
      lineHeight: '1.4',
    },
    subTeamStats: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '16px',
    },
    memberCount: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      color: '#4a5568',
      fontSize: isMobile ? '0.8rem' : '0.9rem',
    },
    memberIcon: {
      width: '16px',
      height: '16px',
      fill: '#4a5568',
    },
    teamName: {
      fontSize: isMobile ? '0.7rem' : '0.8rem',
      color: '#8b5cf6',
      fontWeight: '500',
      marginBottom: '8px',
    },
    subTeamActions: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
    },
    cardButton: {
      padding: isMobile ? '6px 10px' : '8px 12px',
      fontSize: isMobile ? '0.7rem' : '0.8rem',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      flex: 1,
      minWidth: 'fit-content',
    },
    editCardButton: {
      backgroundColor: '#4a6de5',
      color: 'white',
    },
    deleteCardButton: {
      backgroundColor: '#fee2e2',
      color: '#dc2626',
    },
    emptySubTeams: {
      textAlign: 'center',
      padding: '40px',
      color: '#718096',
      fontSize: '1.1rem',
      fontStyle: 'italic',
      background: 'white',
      borderRadius: '12px',
      border: '2px dashed #e2e8f0',
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#718096',
      fontSize: '1.1rem',
    },
    error: {
      background: '#fed7d7',
      color: '#c53030',
      padding: '12px',
      borderRadius: '6px',
      marginBottom: '15px',
      fontSize: '0.9rem',
    },
    editForm: {
      background: '#f7fafc',
      borderRadius: '6px',
      padding: '12px',
      marginTop: '12px',
      border: '1px solid #e2e8f0',
    },
  };

  // Sub-Teams laden
  const loadSubTeams = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getSubTeams(user.id, currentOrganization.id);
      
      if (response.success) {
        setSubTeams(response.subTeams);
      } else {
        setError(response.error || 'Fehler beim Laden der Sub-Teams');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Organisations-Details laden
  const loadOrganizationDetails = async () => {
    if (!organizationId) return;
    
    try {
      const response = await getOrganization(user.id, organizationId);
      if (response.success) {
        setOrganizationDetails(response.organization);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Organisations-Details:', err);
    }
  };

  // Teams laden
  const loadTeams = async () => {
    try {
      const response = await getTeamsByOrganization(user.id, currentOrganization.id);
      if (response.success) {
        setTeams(response.teams);
        if (response.teams.length > 0) {
          setSelectedTeamId(response.teams[0].id);
        }
      }
    } catch (err) {
      console.error('Fehler beim Laden der Teams:', err);
    }
  };



  // Neues Sub-Team erstellen
  const handleCreateSubTeam = async (e) => {
    e.preventDefault();
    
    if (!newSubTeamName.trim() || !selectedTeamId) {
      setError('Bitte geben Sie einen Sub-Team-Namen ein und wählen Sie ein Team aus.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await createSubTeam(
        user.id, 
        currentOrganization.id, 
        selectedTeamId, 
        newSubTeamName, 
        newSubTeamDescription
      );
      
      if (response.success) {
        setNewSubTeamName('');
        setNewSubTeamDescription('');
        setShowCreateSubTeam(false);
        loadSubTeams(); // Sub-Teams neu laden
      } else {
        setError(response.error || 'Fehler beim Erstellen des Sub-Teams');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Sub-Team bearbeiten
  const handleEditSubTeam = async (e) => {
    e.preventDefault();
    
    if (!editName.trim()) {
      setError('Bitte geben Sie einen Sub-Team-Namen ein.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await updateSubTeam(
        user.id, 
        editingSubTeam.id, 
        editName, 
        editDescription
      );
      
      if (response.success) {
        setEditingSubTeam(null);
        setEditName('');
        setEditDescription('');
        loadSubTeams(); // Sub-Teams neu laden
      } else {
        setError(response.error || 'Fehler beim Aktualisieren des Sub-Teams');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Sub-Team löschen
  const handleDeleteSubTeam = async (subTeamId, subTeamName) => {
    if (!window.confirm(`Sind Sie sicher, dass Sie das Sub-Team "${subTeamName}" löschen möchten? Alle Benutzerzuordnungen werden entfernt.`)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await deleteSubTeam(user.id, subTeamId);
      
      if (response.success) {
        loadSubTeams(); // Sub-Teams neu laden
      } else {
        setError(response.error || 'Fehler beim Löschen des Sub-Teams');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Bearbeitung starten
  const startEditing = (subTeam) => {
    setEditingSubTeam(subTeam);
    setEditName(subTeam.name);
    setEditDescription(subTeam.description || '');
  };

  // Bearbeitung abbrechen
  const cancelEditing = () => {
    setEditingSubTeam(null);
    setEditName('');
    setEditDescription('');
  };

  // Text kürzen für mobile Anzeige
  const truncateText = (text, maxLength) => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Beim ersten Laden laden
  useEffect(() => {
    loadSubTeams();
    loadTeams();
    loadOrganizationDetails();
  }, [currentOrganization.id, organizationId]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        {onBack && (
          <button 
            style={styles.backButton}
            onClick={onBack}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#3c5ce0';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#4a6de5';
            }}
          >
            ← Zurück
          </button>
        )}
        <h2 style={styles.title}>
          Sub-Teams verwalten - {organizationDetails?.name || currentOrganization.name}
        </h2>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Sub-Teams Sektion - nur für Admin/OrgLeiter anzeigen */}
      {(isAdmin || isOrgLeader) && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>Sub-Teams verwalten</h3>
            <div style={styles.buttonGroup}>
              <button
                style={styles.createButton}
                onClick={() => setShowCreateSubTeam(!showCreateSubTeam)}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#078a1a';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#0a9a22';
                }}
              >
                {showCreateSubTeam ? 'Abbrechen' : '+ Neues Sub-Team'}
              </button>
            </div>
          </div>

          {showCreateSubTeam && (
            <form onSubmit={handleCreateSubTeam} style={styles.createForm}>
              <select
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                style={styles.formSelect}
                required
              >
                <option value="">Team auswählen</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Sub-Team-Name eingeben"
                value={newSubTeamName}
                onChange={(e) => setNewSubTeamName(e.target.value)}
                style={styles.formInput}
                required
              />
              <textarea
                placeholder="Beschreibung (optional)"
                value={newSubTeamDescription}
                onChange={(e) => setNewSubTeamDescription(e.target.value)}
                style={styles.formTextarea}
              />
              <button type="submit" style={{...styles.formButton, ...styles.submitButton}}>
                Erstellen
              </button>
              <button 
                type="button" 
                onClick={() => {
                  setShowCreateSubTeam(false);
                  setNewSubTeamName('');
                  setNewSubTeamDescription('');
                }}
                style={{...styles.formButton, ...styles.cancelButton}}
              >
                Abbrechen
              </button>
            </form>
          )}

          {/* Sub-Teams-Grid */}
          {loading ? (
            <div style={styles.loading}>Sub-Teams werden geladen...</div>
          ) : (
            <div style={styles.subTeamsGrid}>
              {subTeams.length === 0 ? (
                <div style={styles.emptySubTeams}>
                  Keine Sub-Teams gefunden
                </div>
              ) : (
                subTeams.map((subTeam) => (
                  <div 
                    key={subTeam.id} 
                    style={styles.subTeamCard}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    <div style={styles.subTeamCardHeader}>
                      <h4 style={styles.subTeamName} title={subTeam.name}>
                        {truncateText(subTeam.name, isMobile ? 12 : isTablet ? 15 : 25)}
                      </h4>
                    </div>
                    
                    <div style={styles.teamName} title={subTeam.team_name}>
                      Team: {truncateText(subTeam.team_name, isMobile ? 15 : 20)}
                    </div>
                    
                    {subTeam.description && (
                      <div style={styles.subTeamDescription} title={subTeam.description}>
                        {truncateText(subTeam.description, isMobile ? 30 : isTablet ? 40 : 60)}
                      </div>
                    )}
                    
                    <div style={styles.subTeamStats}>
                      <span style={styles.memberCount}>
                        <svg style={styles.memberIcon} viewBox="0 0 24 24">
                          <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2.01 1l-1.7 2.26A6.003 6.003 0 0 0 10 16v6h10zm-8-2v-4c0-1.1.9-2 2-2s2 .9 2 2v4h-4z"/>
                        </svg>
                        {subTeam.member_count || 0} Mitglieder
                      </span>
                    </div>
                    
                    <div style={styles.subTeamActions}>
                      <button
                        style={{...styles.cardButton, ...styles.editCardButton}}
                        onClick={() => startEditing(subTeam)}
                        title="Sub-Team bearbeiten"
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#3c5ce0';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#4a6de5';
                        }}
                      >
                        {isMobile ? 'Bearb.' : 'Bearbeiten'}
                      </button>
                      <button
                        style={{...styles.cardButton, ...styles.deleteCardButton}}
                        onClick={() => handleDeleteSubTeam(subTeam.id, subTeam.name)}
                        title="Sub-Team löschen"
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#fecaca';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#fee2e2';
                        }}
                      >
                        {isMobile ? '✕' : 'Löschen'}
                      </button>
                    </div>

                    {/* Bearbeitungsformular */}
                    {editingSubTeam && editingSubTeam.id === subTeam.id && (
                      <form onSubmit={handleEditSubTeam} style={styles.editForm}>
                        <input
                          type="text"
                          placeholder="Sub-Team-Name"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          style={{...styles.formInput, marginBottom: '8px'}}
                          required
                        />
                        <textarea
                          placeholder="Beschreibung (optional)"
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          style={{...styles.formTextarea, marginBottom: '8px'}}
                        />
                        <div style={{display: 'flex', gap: '8px'}}>
                          <button type="submit" style={{...styles.formButton, ...styles.submitButton}}>
                            Speichern
                          </button>
                          <button 
                            type="button" 
                            onClick={cancelEditing}
                            style={{...styles.formButton, ...styles.cancelButton}}
                          >
                            Abbrechen
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}


    </div>
  );
}

export default GlobalSubTeamManagement; 